import type { Card, CardColor, HickroGame } from "./types.js";
import { createDeck, shuffle, canPlay } from "./types.js";

export function createGame(playerNames: string[]): HickroGame {
  const deck = createDeck();
  const hands: Card[][] = playerNames.map(() => []);
  const numPlayers = playerNames.length;

  let di = 0;
  for (let i = 0; i < 7; i++) {
    for (let p = 0; p < numPlayers; p++) {
      hands[p].push(deck[di++]);
    }
  }

  let topCard = deck[di++];
  while (topCard.color === "wild") {
    deck.push(topCard);
    topCard = deck[di++];
  }

  const remaining = deck.slice(di);

  return {
    deck: remaining,
    discard: [topCard],
    players: playerNames.map((name, i) => ({ hand: hands[i], name })),
    currentPlayer: 0,
    direction: 1,
    topCard,
    winner: null,
    pendingDraw: 0,
    skipped: false,
  };
}

export function drawCards(game: HickroGame, count: number): HickroGame {
  let deck = [...game.deck];
  const discard = [...game.discard];
  const players = game.players.map((p) => ({ ...p, hand: [...p.hand] }));

  const drawn: Card[] = [];
  for (let i = 0; i < count; i++) {
    if (deck.length === 0) {
      const top = discard.pop()!;
      deck = shuffle(discard);
      discard.length = 0;
      discard.push(top);
      if (deck.length === 0) break;
    }
    drawn.push(deck.pop()!);
  }

  players[game.currentPlayer].hand.push(...drawn);

  const nextPlayer = (game.currentPlayer + game.direction + game.players.length) % game.players.length;

  return {
    ...game, deck, players,
    currentPlayer: nextPlayer,
    pendingDraw: 0,
    skipped: false,
  };
}

export function playCard(game: HickroGame, cardIndex: number, declaredColor?: CardColor): HickroGame {
  const player = game.players[game.currentPlayer];
  const card = player.hand[cardIndex];
  if (!card) return game;
  if (!canPlay(card, game.topCard!)) return game;

  const players = game.players.map((p) => ({ ...p, hand: [...p.hand] }));
  const discard = [...game.discard];
  const deck = [...game.deck];

  const [played] = players[game.currentPlayer].hand.splice(cardIndex, 1);

  const resolvedColor = card.color === "wild" && declaredColor ? declaredColor : card.color;
  const topCard: Card = card.color === "wild" ? { ...card, color: declaredColor ?? "red" } : card;

  discard.push(topCard);

  let nextPlayer = (game.currentPlayer + game.direction + game.players.length) % game.players.length;
  let direction = game.direction;
  let pendingDraw = 0;
  let skipped = false;

  if (card.value === "skip") {
    nextPlayer = (nextPlayer + direction + game.players.length) % game.players.length;
    skipped = true;
  } else if (card.value === "reverse") {
    direction = (direction * -1) as 1 | -1;
    if (game.players.length === 2) {
      nextPlayer = (nextPlayer + direction + game.players.length) % game.players.length;
    }
  } else if (card.value === "draw2") {
    pendingDraw = 2;
  } else if (card.value === "wild4") {
    pendingDraw = 4;
  }

  const winner = players[game.currentPlayer].hand.length === 0 ? game.currentPlayer : null;

  return {
    ...game, players, discard, deck, topCard,
    currentPlayer: nextPlayer,
    direction,
    winner,
    pendingDraw,
    skipped,
  };
}

export function getAIMove(game: HickroGame): { cardIndex: number; declaredColor?: CardColor } | null {
  const player = game.players[game.currentPlayer];

  const playable = player.hand
    .map((c, i) => ({ card: c, index: i }))
    .filter(({ card }) => canPlay(card, game.topCard!));

  if (playable.length === 0) return null;

  const wilds = playable.filter(({ card }) => card.color === "wild");
  const normals = playable.filter(({ card }) => card.color !== "wild");

  if (normals.length > 0) {
    const byColor: Record<string, number> = {};
    for (const c of player.hand) {
      if (c.color !== "wild") byColor[c.color] = (byColor[c.color] || 0) + 1;
    }
    let bestColor = "red";
    let bestCount = 0;
    for (const [color, count] of Object.entries(byColor)) {
      if (count > bestCount) { bestColor = color; bestCount = count; }
    }

    const draw2 = normals.find(({ card }) => card.value === "draw2");
    const skip = normals.find(({ card }) => card.value === "skip");
    const high = normals.reduce((a, b) =>
      typeof b.card.value === "number" && (typeof a.card.value !== "number" || b.card.value > a.card.value) ? b : a
    );

    const chosen = draw2 || skip || high;
    return { cardIndex: chosen.index };
  }

  const byColor: Record<string, number> = {};
  for (const c of player.hand) {
    if (c.color !== "wild") byColor[c.color] = (byColor[c.color] || 0) + 1;
  }
  let bestColor = "red";
  let bestCount = 0;
  for (const [color, count] of Object.entries(byColor)) {
    if (count > bestCount) { bestColor = color; bestCount = count; }
  }

  const wild4 = wilds.find(({ card }) => card.value === "wild4");
  const chosen = wild4 || wilds[0];
  return { cardIndex: chosen.index, declaredColor: bestColor as CardColor };
}

export const CARD_EMOJIS: Record<string, string> = {
  "0": "0", "1": "1", "2": "2", "3": "3", "4": "4",
  "5": "5", "6": "6", "7": "7", "8": "8", "9": "9",
  "skip": "⊘", "reverse": "⟲", "draw2": "+2",
  "wild": "★", "wild4": "+4",
};
